import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ChosenSpec, ISpecGroup, reasonTypes } from '@/types/product';

import { getProduct, getProductsByCategoryId } from '../../../../../routes/products';
import { delay, slug } from '../../../../../services/global';
import {
  filterValidFullProducts,
  fpIsAvailable,
  productIsAvailable,
} from '../../../../../services/products';
import { ApplicationState } from '../../../../../store';
import { IOrder } from '../../../../../store/ducks/order/types';
import { cleanData, setChosenProduct } from '../../../../../store/ducks/products/actions';
import { IProduct } from '../../../../../store/ducks/products/types';
import { useViewItem } from '../../../../hooks/useViewItem';
import { useWebContext } from '../../../../hooks/useWebContext';
import {
  fillChosenSpec,
  fillSpecs,
  handleChangeSpec as handleChangeSpecUtil,
} from '../utils/productUtils';

export const useProductLogic = (productId: string, name: string) => {
  const { shop, selectBase64 } = useWebContext();
  const history = useHistory();
  const dispatch = useDispatch();

  // Selectors
  const selectOrder = useSelector<ApplicationState, IOrder | null>((state) => state.order.data);
  const selectShopIdDst = useSelector<ApplicationState, number | null>(
    (state) => state.order.data?.dst_shop_id ?? null,
  );
  const selectChosenFullProduct = useSelector<ApplicationState, IProduct | undefined>(
    (state) => state.productsGroupByCategory.chosenProduct,
  );
  const selectSpecProducts = useSelector<ApplicationState, IProduct[]>(
    (state) => state.productsGroupByCategory.specProducts,
  );
  const selectProductsGroupedByCategory = useSelector<ApplicationState, any[]>(
    (state) => state.productsGroupByCategory.data,
  );
  const selectProductsLoading = useSelector<ApplicationState, boolean>(
    (state) => state.productsGroupByCategory.loading,
  );
  const selectProductLoading = useSelector<ApplicationState, boolean>(
    (state) => state.productsGroupByCategory.productLoading,
  );

  // State
  const [specs, setSpecs] = useState<{ size: string | undefined; groups: ISpecGroup[] }[]>([]);
  const [chosenSpec, setChosenSpec] = useState<ChosenSpec>(null);
  const [fullProducts, setFullProducts] = useState<IProduct[]>([]);
  const [isAvailable, setIsAvailable] = useState<reasonTypes>('available');
  const [isAllAvailable, setAllIsAvailable] = useState<reasonTypes>('available');

  // Track if we've already initialized this product to prevent infinite loops
  const initializedProductRef = useRef<string | null>(null);
  const selectOrderRef = useRef(selectOrder);

  // Update the ref when selectOrder changes, but don't create dependencies
  useEffect(() => {
    selectOrderRef.current = selectOrder;
  }, [selectOrder]);

  const cartItem = useMemo(() => {
    return selectOrder?.cart.products.find((p) => productId && p.product_id === +productId) ?? null;
  }, [selectOrder?.cart.products, productId]);

  const handleChangeSpec = useCallback(
    ({ size, group = null }: { size: string | undefined; group: ISpecGroup | null }) => {
      handleChangeSpecUtil({ size, group }, specs, setChosenSpec);
    },
    [specs],
  );

  const initializeProduct = useCallback(() => {
    // Prevent multiple initializations for the same product
    const productKey = `${productId}-${name}`;
    if (initializedProductRef.current === productKey) {
      return;
    }

    if (selectShopIdDst && productId) {
      initializedProductRef.current = productKey;

      getProduct(selectShopIdDst, +productId).then(({ fullProducts, specProducts }) => {
        setSpecs([]);
        setChosenSpec(null);
        setFullProducts(fullProducts);

        if (!name && fullProducts && fullProducts.length > 0) {
          const title = slug(fullProducts[0]?.title ?? '');
          history.push(`/product/${productId}/${encodeURIComponent(title)}`);
          return;
        }

        const validFullProducts = filterValidFullProducts(fullProducts, shop);

        if (validFullProducts.length > 0) dispatch(setChosenProduct(validFullProducts[0]));

        if (specProducts.length > 0) {
          fillSpecs(validFullProducts, setSpecs);
          // Get cart item at the time of initialization only - get fresh data from ref
          const currentOrder = selectOrderRef.current;
          const currentCartItem =
            currentOrder?.cart.products.find((p) => p.product_id === +productId) ?? null;
          fillChosenSpec(validFullProducts, shop, currentCartItem, setChosenSpec);
        }

        if (validFullProducts[0]) useViewItem(validFullProducts[0]);
      });
    }
  }, [selectShopIdDst, productId, name, history, dispatch, shop]);

  const fillProductCategories = useCallback(() => {
    if (selectShopIdDst && selectChosenFullProduct && selectChosenFullProduct.category_id) {
      const alreadyHasCategory = selectProductsGroupedByCategory.find(
        (categoryGroup) => categoryGroup.id === selectChosenFullProduct.category_id,
      );

      if (!alreadyHasCategory) {
        getProductsByCategoryId(selectShopIdDst, selectChosenFullProduct.category_id);
      }
    }
  }, [selectShopIdDst, selectChosenFullProduct, selectProductsGroupedByCategory]);

  const setChosenProductWhenChangingSpecs = useCallback(() => {
    if (selectSpecProducts.length > 0 && chosenSpec) {
      const fullProduct = selectSpecProducts.find((fp) => fp.id === chosenSpec.group.fullProductId);
      if (fullProduct) dispatch(setChosenProduct(fullProduct));
    }
  }, [selectSpecProducts, chosenSpec, dispatch]);

  useEffect(() => {
    initializeProduct();
  }, [initializeProduct]);

  useEffect(() => {
    // Only run when categories are needed and category ID exists
    if (selectChosenFullProduct?.category_id && selectShopIdDst) {
      fillProductCategories();
    }
  }, [selectChosenFullProduct?.category_id, selectShopIdDst]);

  useEffect(() => {
    // Only run when chosen spec changes
    if (chosenSpec?.group?.fullProductId) {
      setChosenProductWhenChangingSpecs();
    }
  }, [chosenSpec?.group?.fullProductId]);

  useEffect(() => {
    if (selectChosenFullProduct && fullProducts) {
      setAllIsAvailable(
        // @ts-ignore - Type compatibility issue between IProduct and IFullProduct
        fpIsAvailable(fullProducts, selectChosenFullProduct.measure, shop),
      );
    }
  }, [selectChosenFullProduct, fullProducts, shop]);

  useEffect(() => {
    if (selectChosenFullProduct) {
      setIsAvailable(
        // @ts-ignore - Type compatibility issue
        productIsAvailable(selectChosenFullProduct, selectChosenFullProduct.measure, shop),
      );
    }
  }, [selectChosenFullProduct, shop]);

  useEffect(() => {
    if (['disabled', 'inactive'].includes(isAllAvailable)) {
      delay(() => {
        history.push('/w');
      }, 3000);
    }
  }, [isAllAvailable, selectBase64]);

  useEffect(() => {
    return () => {
      setSpecs([]);
      setChosenSpec(null);
      initializedProductRef.current = null;
      dispatch(cleanData());
    };
  }, [dispatch, productId]);

  const productsByCategoryFiltered = useMemo(() => {
    return selectProductsGroupedByCategory.map((category) => {
      const filteredProducts = category.products.filter(
        (product: IProduct) => product.ean !== selectChosenFullProduct?.ean,
      );
      return { ...category, products: filteredProducts };
    });
  }, [selectProductsGroupedByCategory, selectChosenFullProduct]);

  return {
    // State
    specs,
    chosenSpec,
    fullProducts,
    cartItem,
    isAvailable,
    isAllAvailable,
    // Selectors
    selectChosenFullProduct,
    selectProductsLoading,
    selectProductLoading,
    productsByCategoryFiltered,
    // Functions
    handleChangeSpec,
  };
};
